import { useEffect, useRef, useState, type FormEvent } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router";
import { useAuth, useCan } from "../../auth/AuthProvider";
import { Icon } from "../../components/icons";
import {
  Alert,
  Button,
  Card,
  LoadingBlock,
  Textarea,
  buttonStyles,
  cx,
} from "../../components/ui";
import { ApiError } from "../../lib/api";
import { absoluteTime, relativeTime } from "../../lib/format";
import {
  isLocked,
  isPreviewable,
} from "../../lib/support";
import {
  fetchAttachment,
  useReplyToTicket,
  useTicket,
  useUploadAttachment,
} from "../../lib/queries";
import type { SupportAttachment, SupportMessage, SupportTicketDetail } from "../../lib/types";
import { AttachmentPicker } from "./AttachmentPicker";
import { AttachmentRow, CategoryChip, PriorityChip, StatusBadge } from "./TicketBits";

/**
 * One ticket, as a conversation.
 *
 * Laid out as a thread with a composer under it rather than as a form with a
 * history panel, because that is what it is — and because the shape people
 * already understand from every messaging surface they use costs nothing to
 * borrow. The facts about the ticket sit in a rail beside it, where they can be
 * read without scrolling past them to reach the last message.
 */
export function TicketDetailPage() {
  const { t } = useTranslation("support");
  const { ticketId } = useParams();
  const { currentOrg } = useAuth();
  const canWrite = useCan("developer");
  const orgId = currentOrg?.id ?? "";
  const location = useLocation();
  const uploadFailedOnCreate = (location.state as { uploadFailed?: string } | null)?.uploadFailed;

  const { data: ticket, isPending, error } = useTicket(orgId || undefined, ticketId);

  if (isPending) return <LoadingBlock label={t("thread.loading")} />;

  if (error || !ticket) {
    return (
      <div className="space-y-5">
        <Alert>
          {error instanceof ApiError && error.status === 404
            ? t("thread.notFound")
            : t("thread.loadFailed")}
        </Alert>
        <Link to="/support" className={buttonStyles("secondary")}>
          {t("thread.back")}
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-[13px] text-fg-subtle">
        <Link to="/support" className="transition-colors hover:text-fg">
          {t("thread.crumb")}
        </Link>
        <Icon name="chevron" className="size-3" />
        <span className="ident text-[12px] text-fg-muted">{ticket.reference}</span>
      </div>

      <header className="space-y-3 border-b border-line pb-5">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={ticket.status} />
          <CategoryChip category={ticket.category} />
          <PriorityChip priority={ticket.priority} />
        </div>
        <h1 className="text-[1.375rem] font-normal tracking-[-0.022em] break-words text-fg">
          {ticket.subject}
        </h1>
        <p className="text-[13px] text-fg-muted">{t(`status.${ticket.status}.hint`)}</p>
      </header>

      {uploadFailedOnCreate && (
        <Alert tone="warning">
          <Trans
            ns="support"
            i18nKey="thread.uploadFailed"
            values={{ filename: uploadFailedOnCreate }}
            components={{ name: <strong /> }}
          />
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_18rem]">
        {/* `min-w-0` on both columns. A grid item's automatic minimum size is
            its min-content, and a textarea contributes its `cols` width to
            that — so without this the single-column phone layout sets a track
            wider than the screen and the whole page scrolls sideways. Measured
            at 71px of overflow on a 390px viewport before this. */}
        <div className="min-w-0 space-y-4">
          <ol className="space-y-4">
            {ticket.messages.map((message) => (
              <li key={message.id}>
                <Message message={message} orgId={orgId} ticketId={ticket.id} />
              </li>
            ))}
          </ol>

          {canWrite ? (
            <Composer ticket={ticket} orgId={orgId} />
          ) : (
            <Card>
              <p className="p-5 text-[13px] text-fg-subtle">
                {t("readOnly")}
              </p>
            </Card>
          )}
        </div>

        <TicketFacts ticket={ticket} />
      </div>
    </div>
  );
}

/**
 * One message.
 *
 * Ours is marked by an accent edge and a named sender; theirs is plain. No
 * alignment trick — a chat layout that pushes one side right reads as a phone
 * app pasted into a console, and it halves the width available to a stack
 * trace, which is what most of these messages are.
 */
function Message({
  message,
  orgId,
  ticketId,
}: {
  message: SupportMessage;
  orgId: string;
  ticketId: string;
}) {
  const { t } = useTranslation("support");
  const fromStaff = message.authorRole === "staff";

  return (
    <article
      className={cx(
        "border bg-raised",
        fromStaff ? "border-accent/25 border-l-2 border-l-accent" : "border-line",
      )}
    >
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2.5">
        <span className="flex items-center gap-2 text-[13px]">
          <span
            className={cx(
              "grid size-5 place-items-center rounded-full text-[10px] font-medium",
              fromStaff ? "bg-accent/15 text-accent" : "bg-fg/10 text-fg-muted",
            )}
            aria-hidden="true"
          >
            {fromStaff ? "M" : (message.authorName?.[0]?.toUpperCase() ?? "?")}
          </span>
          <span className={fromStaff ? "font-medium text-accent" : "font-medium text-fg"}>
            {message.authorName ?? t("thread.formerMember")}
          </span>
        </span>
        <time
          dateTime={message.createdAt}
          title={absoluteTime(message.createdAt)}
          className="text-[12px] text-fg-subtle"
        >
          {relativeTime(message.createdAt)}
        </time>
      </header>

      {/* `whitespace-pre-wrap` and nothing else: message bodies are plain text
          by construction — the API refuses markup — and a stack trace pasted
          into a paragraph that reflows is unreadable. */}
      <p className="px-4 py-3.5 text-[13px] leading-6 whitespace-pre-wrap break-words text-fg-muted">
        {message.body}
      </p>

      {message.attachments.length > 0 && (
        <div className="space-y-2 border-t border-line px-4 py-3">
          {message.attachments.map((attachment) => (
            <Attachment
              key={attachment.id}
              attachment={attachment}
              orgId={orgId}
              ticketId={ticketId}
            />
          ))}
        </div>
      )}
    </article>
  );
}

/**
 * An attachment, fetched with the session's token when somebody asks for it.
 *
 * Images expand in place; everything else downloads. The object URL is revoked
 * when the component goes away — without that, every image opened in a long
 * thread stays in memory for the life of the tab.
 */
function Attachment({
  attachment,
  orgId,
  ticketId,
}: {
  attachment: SupportAttachment;
  orgId: string;
  ticketId: string;
}) {
  const { t } = useTranslation("support");
  const [url, setUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const objectUrl = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );

  const open = async () => {
    if (busy) return;
    setBusy(true);
    setFailed(false);
    try {
      const blob = await fetchAttachment(orgId, ticketId, attachment.id);
      const href = URL.createObjectURL(blob);
      objectUrl.current = href;

      if (isPreviewable(attachment.contentType)) {
        setUrl(href);
      } else {
        // A download the browser performs itself, from a blob this page already
        // holds — the API cannot be linked to directly, because an anchor
        // carries no authorization header.
        const anchor = document.createElement("a");
        anchor.href = href;
        anchor.download = attachment.filename;
        anchor.click();
      }
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <AttachmentRow attachment={attachment} onOpen={open} busy={busy} />
      {failed && <p className="text-[12px] text-danger">{t("thread.fetchFailed")}</p>}
      {url && (
        <img
          src={url}
          alt={attachment.filename}
          className="max-h-96 w-full border border-line object-contain"
        />
      )}
    </div>
  );
}

/** The reply box, or the reason there is not one. */
function Composer({ ticket, orgId }: { ticket: SupportTicketDetail; orgId: string }) {
  const { t } = useTranslation("support");
  const reply = useReplyToTicket(orgId, ticket.id);
  const upload = useUploadAttachment(orgId);
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploadFailed, setUploadFailed] = useState<string | null>(null);

  const busy = reply.isPending || upload.isPending;

  if (isLocked(ticket)) {
    return (
      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3 p-5">
          <p className="text-[13px] text-fg-muted">
            {t("closed")}
          </p>
          <Link to="/support/new" className={buttonStyles("secondary")}>
            {t("openOne")}
          </Link>
        </div>
      </Card>
    );
  }

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || body.trim().length === 0) return;

    const message = await reply.mutateAsync(body.trim());

    let failed: string | null = null;
    for (const file of files) {
      try {
        await upload.mutateAsync({ ticketId: ticket.id, messageId: message.id, file });
      } catch {
        failed = file.name;
      }
    }

    setUploadFailed(failed);
    setBody("");
    setFiles([]);
  };

  return (
    <Card>
      <form onSubmit={submit} className="space-y-4 p-5">
        {reply.error && (
          <Alert>
            {reply.error instanceof ApiError
              ? reply.error.message
              : t("thread.replyFailed")}
          </Alert>
        )}
        {uploadFailed && (
          <Alert tone="warning">
            <Trans
              ns="support"
              i18nKey="thread.replyUploadFailed"
              values={{ name: uploadFailed }}
              components={{ name: <strong /> }}
            />
          </Alert>
        )}

        {/* The reopen warning lives beside the send button rather than in the
            placeholder as well: said in both, a resolved ticket says it twice
            while the box is empty, and loses it at the moment it matters. */}
        <Textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={5}
          maxLength={10_000}
          placeholder={t("thread.replyPlaceholder")}
          aria-label={t("thread.replyLabel")}
        />

        <AttachmentPicker files={files} onChange={setFiles} disabled={busy} />

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" variant="primary" disabled={busy || body.trim().length === 0}>
            <Icon name="send" className="size-3.5" />
            {busy ? t("thread.sending") : t("thread.send")}
          </Button>
          {ticket.status === "resolved" && (
            <span className="text-[13px] text-fg-subtle">{t("thread.reopens")}</span>
          )}
        </div>
      </form>
    </Card>
  );
}

/** The facts, in a rail: what it is, who filed it, what it is about. */
function TicketFacts({ ticket }: { ticket: SupportTicketDetail }) {
  const { t } = useTranslation("support");
  const workload = ticket.appId
    ? { label: ticket.appName ?? t("thread.app"), to: `/apps/${ticket.appId}`, icon: "apps" as const }
    : ticket.databaseId
      ? {
          label: ticket.databaseName ?? t("thread.database"),
          to: `/databases/${ticket.databaseId}`,
          icon: "database" as const,
        }
      : null;

  return (
    <aside className="min-w-0 space-y-4">
      <div className="card p-5">
        <h2 className="text-[13px] font-medium text-fg">{t("thread.facts")}</h2>
        <dl className="mt-3 space-y-2.5 text-[13px]">
          <Fact label={t("thread.reference")}>
            <span className="ident text-[12px]">{ticket.reference}</span>
          </Fact>
          <Fact label={t("thread.opened")}>
            <span title={absoluteTime(ticket.createdAt)}>{relativeTime(ticket.createdAt)}</span>
          </Fact>
          <Fact label={t("thread.openedBy")}>{ticket.openedByName ?? "—"}</Fact>
          <Fact label={t("thread.about")}>{t(`category.${ticket.category}.label`)}</Fact>
          <Fact label={t("thread.impact")}>{t(`priority.${ticket.priority}.label`)}</Fact>
          <Fact label={t("thread.lastActivity")}>
            <span title={absoluteTime(ticket.lastMessageAt)}>
              {relativeTime(ticket.lastMessageAt)}
            </span>
          </Fact>
          {ticket.resolvedAt && (
            <Fact label={t("thread.resolved")}>
              <span title={absoluteTime(ticket.resolvedAt)}>
                {relativeTime(ticket.resolvedAt)}
              </span>
            </Fact>
          )}
        </dl>

        {workload && (
          <Link
            to={workload.to}
            className="mt-4 flex items-center gap-2 border border-line px-2.5 py-2 text-[13px] text-fg-muted transition-colors duration-200 ease-smooth hover:border-line-strong hover:text-fg"
          >
            <Icon name={workload.icon} className="size-3.5 shrink-0 text-fg-subtle" />
            <span className="min-w-0 flex-1 truncate">{workload.label}</span>
            <Icon name="arrow-up-right" className="size-3 shrink-0 text-fg-subtle" />
          </Link>
        )}
      </div>

      <p className="text-[13px] leading-6 text-fg-subtle">{t("thread.footer")}</p>
    </aside>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="min-w-0 truncate text-right text-fg-muted">{children}</dd>
    </div>
  );
}
